import { fromEntries } from "./browserCompat";
const SECTION_CODES = {
  navbar: "n",
  hero: "h",
  spotlight: "s",
  featured: "f",
  brands: "b",
  categories: "g",
  about: "a",
  highlights: "x",
  imageCarousel: "i",
  testimonials: "t",
  contact: "c",
  footer: "o",
  custom: "u",
};

const CODE_SECTIONS = fromEntries(
  Object.entries(SECTION_CODES).map(([section, code]) => [code, section])
);

const DEFAULT_STYLE = {
  titleSize: 2,
  imageSize: 2,
  spacing: 2,
  textStyle: 2,
};

function level(value, fallback = 2) {
  const parsed = Number(value);
  return [1, 2, 3].includes(parsed) ? parsed : fallback;
}


const IMAGE_POSITION_ALIASES = Object.freeze({
  center: "center", centre: "center", middle: "center",
  "center center": "center", "50% 50%": "center",
  top: "top", "top center": "top", "center top": "top",
  bottom: "bottom", "bottom center": "bottom", "center bottom": "bottom",
  left: "left", "left center": "left", "center left": "left",
  right: "right", "right center": "right", "center right": "right",
});

export function normalizeImagePosition(value, fallback = "center") {
  const key = String(value || "").trim().toLowerCase();
  const fallbackKey = String(fallback || "center").trim().toLowerCase();
  return IMAGE_POSITION_ALIASES[key] || IMAGE_POSITION_ALIASES[fallbackKey] || "center";
}

export function parseSegmentVisualStyles(template) {
  const raw = String(template || "catalog-basic");
  const encoded = raw.includes("@") ? raw.slice(raw.indexOf("@") + 1) : "";
  const styles = {};
  const pattern = /([nhsfgbaxcouit])([123])([123])([123])([123])/g;
  let match;

  while ((match = pattern.exec(encoded.replace(/\s+/g, "")))) {
    const section = CODE_SECTIONS[match[1]];
    if (!section) continue;
    styles[section] = {
      titleSize: level(match[2]),
      imageSize: level(match[3]),
      spacing: level(match[4]),
      textStyle: level(match[5]),
    };
  }
  return styles;
}


function splitVisualTemplate(template) {
  const raw = String(template || "catalog-basic").trim() || "catalog-basic";
  const marker = raw.indexOf("@");
  return {
    base: (marker >= 0 ? raw.slice(0, marker) : raw) || "catalog-basic",
    encoded: marker >= 0 ? raw.slice(marker + 1) : "",
  };
}

export function updateSegmentVisualStyle(template, sectionId, patch = {}) {
  if (!SECTION_CODES[sectionId]) return String(template || "catalog-basic");
  const { base } = splitVisualTemplate(template);
  const current = parseSegmentVisualStyles(template);
  const nextStyle = {
    ...DEFAULT_STYLE,
    ...(current[sectionId] || {}),
    ...(patch || {}),
  };
  nextStyle.titleSize = level(nextStyle.titleSize);
  nextStyle.imageSize = level(nextStyle.imageSize);
  nextStyle.spacing = level(nextStyle.spacing);
  nextStyle.textStyle = level(nextStyle.textStyle);

  const isDefault = Object.keys(DEFAULT_STYLE).every((key) => nextStyle[key] === DEFAULT_STYLE[key]);
  if (isDefault) delete current[sectionId];
  else current[sectionId] = nextStyle;

  const encoded = Object.keys(SECTION_CODES).map((section) => {
    const style = current[section];
    if (!style) return "";
    return `${SECTION_CODES[section]}${level(style.titleSize)}${level(style.imageSize)}${level(style.spacing)}${level(style.textStyle)}`;
  }).join("");
  return encoded ? `${base}@${encoded}`.slice(0, 80) : base.slice(0, 80);
}

export function getSegmentVisualStyle(theme, sectionId) {
  return {
    ...DEFAULT_STYLE,
    ...(parseSegmentVisualStyles(theme?.template)[sectionId] || {}),
  };
}

function scale(value, kind) {
  const selected = level(value);
  const scales = {
    title: { 1: 0.78, 2: 0.9, 3: 1.05 },
    image: { 1: 0.72, 2: 0.88, 3: 1.05 },
    spacing: { 1: 0.76, 2: 0.88, 3: 1.02 },
  };
  return scales[kind][selected];
}

export function segmentCssVariables(theme, sectionId) {
  const visual = getSegmentVisualStyle(theme, sectionId);
  const titleScale = scale(visual.titleSize, "title");
  const imageScale = scale(visual.imageSize, "image");
  const spacingScale = scale(visual.spacing, "spacing");
  const textStyles = {
    1: { weight: 760, spacing: "-.012em" },
    2: { weight: 900, spacing: "-.03em" },
    3: { weight: 950, spacing: "-.055em" },
  };
  const text = textStyles[level(visual.textStyle)] || textStyles[2];
  const vars = {
    "--wp-segment-image-scale": imageScale,
    "--wp-segment-title-weight": text.weight,
    "--wp-segment-letter-spacing": text.spacing,
    "--wp-hero-title-mobile": `${3.4 * titleScale}rem`,
    "--wp-hero-title-desktop": `${6 * titleScale}rem`,
    "--wp-navbar-title-mobile": `${1.12 * titleScale}rem`,
    "--wp-navbar-title-md": `${1.25 * titleScale}rem`,
    "--wp-navbar-title-lg": `${1.4 * titleScale}rem`,
  };

  [24, 30, 31, 32, 34, 40, 42, 44, 45, 46, 52, 54, 64, 72].forEach((size) => {
    vars[`--wp-title-${size}`] = `${Math.round(size * titleScale * 100) / 100}px`;
  });
  [40, 48, 56, 64, 72, 80].forEach((size) => {
    vars[`--wp-space-${size}`] = `${Math.round(size * spacingScale * 100) / 100}px`;
  });
  [48, 62, 76, 122, 132, 150, 160, 165, 190, 205, 230, 260, 300, 340, 360, 520].forEach((size) => {
    vars[`--wp-image-${size}`] = `${Math.round(size * imageScale * 100) / 100}px`;
  });

  return vars;
}

export function segmentVisualScale(theme, sectionId, kind = "title") {
  const visual = getSegmentVisualStyle(theme, sectionId);
  return scale(visual[kind === "title" ? "titleSize" : kind === "image" ? "imageSize" : "spacing"], kind);
}

function scaleCssToken(token, factor) {
  if (typeof token !== "string") return token;
  return token.replace(/(-?\d*\.?\d+)(rem|px|vw|vh|svh)/g, (_, number, unit) => {
    const scaled = Math.round(Number(number) * factor * 1000) / 1000;
    return `${scaled}${unit}`;
  });
}

export function scaleResponsiveCss(value, factor) {
  if (value == null) return value;
  if (typeof value === "string") return scaleCssToken(value, factor);
  if (typeof value === "number") return value * factor;
  if (Array.isArray(value)) return value.map((item) => scaleResponsiveCss(item, factor));
  if (typeof value === "object") {
    return fromEntries(
      Object.entries(value).map(([key, item]) => [key, scaleResponsiveCss(item, factor)])
    );
  }
  return value;
}
